import { MergeOrUpdateStateAction, useAsyncFetch, useMergeableState } from '@tableau/vizportalclient-common-components';
import {
  ButtonType,
  DialogPositioningBehavior,
  DialogType,
  DialogWidget,
  TabStyles,
} from '@tableau/widgets';
import React from 'react';
import shallowEqual from 'shallowequal';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';

import { dialogBodyStyle } from '../../catalog/styles/ExternalContentActionsStyles';
import { cssClass } from '../../style';
import { ButtonWithSpinner } from '../components/ButtonWithSpinner';
import { CloseAndDismiss, ModalInstance, ReactModalOptions, ReactModalService } from './ReactModalService';
import { TabDialogWidth } from './style/TabDialogWidth';

/**
 * Options for a React-based "action dialog", similar to the old Angular `IConfirmActionDialogOptions`.
 *
 * These options might be incomplete; not all the Angular functionality is implemented in React.
 * If you need more functionality, add it!
 */
export type ReactActionDialogOptions<P extends Object, S, TResult> = {
  /**
   * The title of the dialog, displayed in its title bar.
   * If title is empty or undefined, then the dialog won't have a title bar.
  */
  readonly title?: string;

  /** The main content of the dialog, where the user can configure the state data that gets passed into the action. */
  readonly bodyComponent: React.ComponentType<ReactActionDialogBodyProps<P, S>>;

  /** The initial value of the dialog's state data. */
  readonly initialDialogState: S;

  /** Sometimes we need to get the initial value of the dialog's state data from server, will overwrite `initialDialogState`. */
  readonly fetchInitialState?: (bodyProps: P, dialogState: S) => Promise<S>;

  /**
   * Additional static information passed to the dialog body component and to the action.
   * Despite being called "initial", these never change while the dialog is open.
   *
   * This prop may be used to avoid passing data to the dialog body and/or action function via closure,
   * thus allowing the those to be unit-tested more easily.
   */
  readonly initialBodyProps: P;

  /** The name of the single button that executes the action. The name might be dependent on the data from the dialog body. */
  readonly actionName: string | ((bodyProps: P, dialogState: S) => string);

  /**
   * The id of the element which further describes the action taken by the dialog.
   * 
   * Used to populate aria-describedby on the action button element.
   */
  readonly actionDescribedBy?: string;

  /**
   * The action to take when the user clicks the Confirm button. The bodyProps and dialogState are
   * passed in.
   *
   * - If the action is successful, the promise should be resolved;
   * - if the action is unsuccessful, the promise should be rejected.
   *
   * The dialog state can be updated from within the action, for example, to set an error message
   * in the dialog body if the action failed. This is useful only if `dismissDialogOnActionFailed` is false.
   */
  action: (bodyProps: P, dialogState: S, updateDialogState: React.Dispatch<MergeOrUpdateStateAction<S>>) => PromiseLike<TResult>;

  /**
   * If provided, it will check this function to see if the action button should be disabled
   * based on the user's selections.
   */
  actionDisabled?: (bodyProps: P, dialogState: S, fetchedState?: S | null) => boolean;

  /**
   * How the dialog should behave if the action promise is rejected.
   * - If dismissDialogOnActionFailed is false, the dialog will stay open, which allows the user to
   *   either dismiss the dialog or select different options and try again.
   * - If dismissDialogOnActionFailed is true, the dialog will automatically dismiss.
   */
  dismissDialogOnActionFailed: boolean;

  /**
   * If true, display the action button as ButtonType.Destructive.
   * False by default.
   */
  isActionDelete?: boolean;

  /**
   * If true, the cancel and action buttons are centered in the dialog.
   * False by default.
   */
  centerActionButtons?: boolean;

  /**
   * The dialog width, use TabDialogWidth.width375px as a default
   */
  dialogWidth: TabDialogWidth;

  /**
   * The dialog height
   */
  dialogHeight?: number;

  /**
   * Optionally pass in style to the dialog body. This is discouraged, as the Dialog Widget
   * is already implemented according to the Visual Design style guidelines.
   */
  dialogBodyStyle?: React.CSSProperties;

  /**
   * When a modal closes from the action menu, keyboard focus is lost and sent to the top of the
   * page. Using this will allow focus to return to the control that opened the modal.
   * This is an accessibility requirement. WCAG Success Criteria: 2.4.3 Focus Order (A)
   */
  shouldReturnFocusOnClose?: boolean;

  /**
   * By default, a dialog focuses the first focusable element within its content upon mount.
   * This is an accessibility requirement, so it should be a very rare case that this prop should be
   * used.
   *
   * To customize which element inside the dialog should be auto-focused, provide this callback,
   * which selects the focusable element from the dialog body. We can use `querySelector` to select
   * an element by type, role, ID, Test ID, or any other CSS selector:
   *
   *     initiallyFocusedElement: (dialog) => dialog.querySelector('#FOCUS_ME')
   *
   * ## PLEASE DISCUSS YOUR SCENARIO IN #dev-shared-widgets BEFORE USING THIS PROP.
   */
  initiallyFocusedElement?: (dialogElement: HTMLElement) => HTMLElement;

  /**
   * Optionally show the dialog close button, by default is false
   */
  showCloseButton?: boolean;

  /**
   * Hide cancel button - by default false
   */
  hideCancelButton?: boolean;

  /**
   * Enables a reset button, on-click will set dialog state to the initial, usually empty, state.
   */
  showResetButtonUsingState?: S;

  /**
   * Optionally display an alternate text on the reset button
   */
  resetButtonText?: string;

  /**
   * Optionally display an alternate text on the dismiss button
   */
  cancelButtonText?: string;

  /**
   * Optionally display a dialog footer component instead of the two-button default footer
   */
  readonly footerComponent?: (closeAndDismiss: CloseAndDismiss<any>, dialogProps: P, dialogState: S) => JSX.Element;
};

/**
 * Props for the client-specified dialog body content. The body is a controlled component, with the
 * dialog data being managed by its parent, so that this data can be passed into the action when the user clicks the button.
 */
export type ReactActionDialogBodyProps<P extends Object, S> = P & {
  /** True when the async action is in progress. Dialogs should disable their input affordances while the action is busy. */
  readonly actionBusy: boolean;

  readonly dialogState: S;

  /**
   * Updates the dialog state. Like the `setState` of class components, this will properly merge
   * partial state objects, or apply the given updater function.
   *
   * If the dialog body component is complicated enough to warrant its state being managed by a
   * reducer, this updater can be attached to a reducer with the `useSetStateAsReducer` hook from
   * @tableau/vizportalclient-common-components.
   */
  readonly updateDialogState: React.Dispatch<MergeOrUpdateStateAction<S>>;

  /**
   * Performs the action, as if the user clicked the action button. This function is provided to the
   * body so that it can be bound to the enter keypress when the certain elements are focused (where
   * this behavior is desired).
   *
   * This function internally evaluates `actionDisabled`, and will do nothing if it evaluates to true.
   */
  readonly doAction: () => void;
};

/**
 * Opens an "action dialog". This is the most common dialog pattern, in which the user is presented with a
 * "Confirm" and "Cancel" button. The dialog also allows client-specified dialog content that lets the user
 * set up some data (the "dialog state") before confirming.
 *
 * ***For simple confirmation dialogs with only body text (no interactive elements except the Confirm/Cancel buttons),
 * use ReactConfirmationDialog.***
 *
 * Upon the user clicking "Confirm", the dialog will block (buttons disabled, dialog remains onscreen)
 * and the action will be performed. If the action is successful, the dialog then closes.
 * If the action is unsuccessful, the behavior depends on the value of the dismissDialogOnActionFailed option
 * (see documentation for the options).
 *
 * This function returns a `ModalInstance` that wraps the entire workflow in a promise:
 * - After successful completion of the action, the dialog closes and the ModalInstance promise is resolved.
 * - If the dialog gets dismissed (via clicking the close button, glass, or cancel button, or after an
 *   unsuccessful action when dismissDialogOnActionFailed is true), the promise gets rejected.
 *   Dismissing the dialog is a normal occurrence, so the calling code should swallow the rejection in most scenarios.
 *   If the dialog is being executed from a Redux saga, this swallowing happens automatically if the rejected promise
 *   propagates up to the root saga (i.e. you don't have to write your own `catch` block).
 */
export function openReactActionDialog<P extends Object, S, TResult>(
  options: ReactActionDialogOptions<P, S, TResult>
): ModalInstance<TResult> {
  return ReactModalService.openCustom(closeAndDismiss =>
    <ReactActionDialog {...options} closeAndDismiss={closeAndDismiss} />
  );
}

type ReactActionDialogProps<P extends Object, S, TResult> = ReactActionDialogOptions<P, S, TResult> & {
  closeAndDismiss: CloseAndDismiss<TResult>;
};

async function asyncFetchInitialState<P extends Object, S extends Object>(
  initialBodyProps: P,
  initialDialogState: S,
  func?: ReactActionDialogProps<P, S, unknown>['fetchInitialState']
): Promise<S | undefined> {
  if (func) {
    const result = await func(initialBodyProps, initialDialogState);
    return result;
  } else {
    return Promise.resolve(undefined);
  }
}

function ReactActionDialog<P extends Object, S, TResult>(props: ReactActionDialogProps<P, S, TResult>): JSX.Element {
  const { fetchInitialState, showResetButtonUsingState } = props;
  const { close: closeDialog, dismiss: dismissDialog } = props.closeAndDismiss;
  const bodyProps = props.initialBodyProps;

  const [fetchedState, setFetchedState] = React.useState<null | S>(null);
  const [dialogState, setDialogState] = useMergeableState(props.initialDialogState);
  const [busy, setBusy] = React.useState<boolean>(false);

  const { data, loading } = useAsyncFetch(asyncFetchInitialState, props.initialBodyProps, props.initialDialogState, fetchInitialState);

  React.useEffect(() => {
    if (fetchInitialState) {
      if (loading) {
        setBusy(true);
      }

      if (data) {
        setDialogState(data);
        setFetchedState(data);
      }

      if (!loading) {
        setBusy(false);
      }
    }
  }, [fetchInitialState, setBusy, setDialogState, setFetchedState, loading, data]);

  const resetAction = React.useCallback(() => {
    if (showResetButtonUsingState) {
      setDialogState(showResetButtonUsingState);
    }
  }, [showResetButtonUsingState, setDialogState]);

  const doAction = React.useCallback(
    async () => {
      if (props.actionDisabled && props.actionDisabled(bodyProps, dialogState, fetchedState)) {
        return;
      }

      setBusy(true);
      try {
        const result = await props.action(bodyProps, dialogState, setDialogState);
        closeDialog(result);
      } catch {
        if (props.dismissDialogOnActionFailed) {
          dismissDialog('cancel');
        } else {
          setBusy(false);
        }
      }
    },
    [props, bodyProps, dialogState, fetchedState, setDialogState, closeDialog, dismissDialog, setBusy]
  );

  const actionName = (typeof props.actionName === 'string') ? props.actionName : props.actionName(bodyProps, dialogState);

  const modalOptions: Omit<ReactModalOptions, 'dialogContent'> = {
    testId: ReactActionDialogTestId,
    title: props.title,
    type: (props.title?.length > 0) ? DialogType.NormalWithTitleBar : DialogType.Uber,
    showCloseButton: props.showCloseButton,
    dismissDialogOnCloseAction: true,
    shouldNotCloseOnGlassClick: busy,
    positioningBehavior: DialogPositioningBehavior.Centered,
    dialogWidth: props.dialogWidth,
    dialogHeight: props.dialogHeight,
    dialogBodyStyle: props.dialogBodyStyle ?? dialogBodyStyle,
    disableAutoFocus: props.initiallyFocusedElement ?? false,
    shouldReturnFocusOnClose: props.shouldReturnFocusOnClose,
  };

  const dialogProps = ReactModalService.getDefaultDialogProps(modalOptions, props.closeAndDismiss);

  const resetButton = props.showResetButtonUsingState && <ButtonWithSpinner
    buttonType={ButtonType.LowEmphasis}
    testId={ReactActionDialogResetButtonTestId}
    children={props.resetButtonText ? props.resetButtonText : Messages.clearSettings()}
    onClick={resetAction}
    disabled={busy || shallowEqual(showResetButtonUsingState, dialogState)}
    className={ResetButtonstyle}
  />;

  const cancelButton = !props.hideCancelButton && <ButtonWithSpinner
    buttonType={ButtonType.Outline}
    onClick={() => dismissDialog('cancel')}
    testId={ReactActionDialogCancelButtonTestId}
    children={props.cancelButtonText ? props.cancelButtonText : Messages.cancel()}
    disabled={busy}
  />;

  const actionButon = <span className={ActionButtonLeftWide}>
    <ButtonWithSpinner
      buttonType={props.isActionDelete ? ButtonType.Destructive : ButtonType.Primary}
      onClick={doAction}
      testId={ReactActionDialogConfirmButtonTestId}
      children={actionName}
      disabled={(!_.isUndefined(props.actionDisabled) && props.actionDisabled(bodyProps, dialogState, fetchedState))}
      showSpinner={busy}
      aria-describedby={props.actionDescribedBy}
    />
  </span>;

  return (
    <DialogWidget {...dialogProps}>
      <props.bodyComponent {...bodyProps} dialogState={dialogState} updateDialogState={setDialogState} actionBusy={busy} doAction={doAction} />
      {props.footerComponent ? props.footerComponent(props.closeAndDismiss, bodyProps, dialogState) :
        <div className={props.centerActionButtons ? ActionButtonsCenter : ActionButtons} >
          {resetButton}
          {cancelButton}
          {actionButon}
        </div>
      }
    </DialogWidget>
  );
}

export const ResetButtonstyle = cssClass('confirm-action-dialog', 'reset-button', {
  marginRight: 'auto !important',
});

export const ActionButtonLeftWide = cssClass('confirm-action-dialog', 'action-button', {
  marginLeft: TabStyles.Spacing.MediumSpace
});

export const actionButtonStyle: React.CSSProperties = {
  marginTop: TabStyles.Spacing.MediumSpace,
  whiteSpace: 'nowrap', // Prevent buttons from wrapping
  textAlign: 'right',
  display: 'flex',
  justifyContent: 'flex-end',
};

export const ActionButtons = cssClass('confirm-action-dialog', 'action-buttons', {
  ...actionButtonStyle,
  $nest: {
    [`.${ResetButtonstyle}`]: { marginRight: 'auto'}
    },
});
const ActionButtonsCenter = cssClass('confirm-action-dialog', 'action-buttons-center', {
  ...actionButtonStyle,
  textAlign: 'center',
  justifyContent: 'space-between',
});

export const ReactActionDialogTestId = 'react-confirm-action-dialog';
export const ReactActionDialogResetButtonTestId = 'confirm-action-dialog-reset';
export const ReactActionDialogCancelButtonTestId = 'confirm-action-dialog-cancel';
export const ReactActionDialogConfirmButtonTestId = 'confirm-action-dialog-confirm';
